import React, { useState } from "react";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";
import ToolLayout from "@/components/ToolLayout";
import PostResultCTA from "@/components/PostResultCTA";
import useSEO from "@/hooks/useSEO";

const QUESTIONS = [
  // Domain 1: Classification
  {
    id: 1,
    domain: "Classification",
    text: "How do you currently classify knowledge sources before using them in AI systems?",
    options: [
      { label: "We don't — all sources are treated the same", score: 1 },
      { label: "We have informal guidelines that some teams follow", score: 2 },
      { label: "We have a documented taxonomy applied consistently", score: 3 },
      { label: "We have a formal classification with automated flagging and audit trail", score: 4 },
    ],
  },
  {
    id: 2,
    domain: "Classification",
    text: "How is PII or sensitive data handled when extracting knowledge from operational sources?",
    options: [
      { label: "It isn't — we don't check for PII before extraction", score: 1 },
      { label: "We manually review obvious cases before extraction", score: 2 },
      { label: "We have automated PII detection and redaction in place", score: 3 },
      { label: "Automated detection plus human review with documented chain of custody", score: 4 },
    ],
  },
  {
    id: 3,
    domain: "Classification",
    text: "Can you trace which source records contributed to any given AI behaviour or rule?",
    options: [
      { label: "No — we have no lineage tracking", score: 1 },
      { label: "Partially — we know the system but not individual records", score: 2 },
      { label: "Yes — we can trace to source documents", score: 3 },
      { label: "Yes — full lineage with timestamps, actors, and evidence records", score: 4 },
    ],
  },
  // Domain 2: Review Process
  {
    id: 4,
    domain: "Review Process",
    text: "How are new knowledge artifacts reviewed before deployment to AI systems?",
    options: [
      { label: "No formal review — deployment is ad hoc", score: 1 },
      { label: "Informal peer review when time allows", score: 2 },
      { label: "Structured review with defined roles and approval steps", score: 3 },
      {
        label:
          "Multi-stage review with SLA targets, decision logging, and role-based approvals",
        score: 4,
      },
    ],
  },
  {
    id: 5,
    domain: "Review Process",
    text: "Do you have defined SLAs for how quickly knowledge artifacts move through review?",
    options: [
      { label: "No SLAs defined", score: 1 },
      { label: "Informal expectations but nothing documented", score: 2 },
      { label: "Documented SLAs by domain or artifact type", score: 3 },
      { label: "SLAs with automated alerts, breach tracking, and reporting", score: 4 },
    ],
  },
  {
    id: 6,
    domain: "Review Process",
    text: "How do reviewers record their decisions and reasoning?",
    options: [
      { label: "They don't — decisions are undocumented", score: 1 },
      { label: "Informal notes in Slack or email", score: 2 },
      { label: "Structured comments in a ticket or system of record", score: 3 },
      { label: "Structured decision log with comment, timestamp, and actor — full audit trail", score: 4 },
    ],
  },
  {
    id: 7,
    domain: "Review Process",
    text: "Can you demonstrate the review process to an auditor with evidence?",
    options: [
      { label: "No — nothing would survive an audit", score: 1 },
      { label: "We could reconstruct most of it with effort", score: 2 },
      { label: "We have records but they're scattered across systems", score: 3 },
      { label: "Yes — centralized, exportable evidence package ready on demand", score: 4 },
    ],
  },
  // Domain 3: Governance
  {
    id: 8,
    domain: "Governance",
    text: "What happens when a deployed knowledge artifact becomes outdated or problematic?",
    options: [
      { label: "Nothing — there's no revocation process", score: 1 },
      { label: "We manually remove or update it when someone notices", score: 2 },
      { label: "We have a documented revocation workflow with impact assessment", score: 3 },
      { label: "Automated freeze, downstream impact analysis, rebuild workflow, audit log", score: 4 },
    ],
  },
  {
    id: 9,
    domain: "Governance",
    text: "Are policies that govern which knowledge can be used in AI systems formally documented?",
    options: [
      { label: "No policies exist", score: 1 },
      { label: "Policies exist but are not enforced consistently", score: 2 },
      { label: "Documented policies with defined scope and approver", score: 3 },
      {
        label:
          "Versioned policies with simulation capabilities, triggered flags, and automated enforcement",
        score: 4,
      },
    ],
  },
  {
    id: 10,
    domain: "Governance",
    text: "How often do you review and update your AI knowledge governance programme?",
    options: [
      { label: "Never / only when forced by an incident", score: 1 },
      { label: "Annually or less", score: 2 },
      { label: "Quarterly with documented outcomes", score: 3 },
      { label: "Continuously — real-time metrics with regular governance board reviews", score: 4 },
    ],
  },
];

const LEVELS = [
  { level: 1, label: "Ad-hoc", range: [10, 16], color: "text-rose-600", bg: "bg-rose-50 border-rose-200", description: "Knowledge governance is informal and reactive. AI behaviour is difficult to explain or audit." },
  { level: 2, label: "Developing", range: [17, 22], color: "text-amber-600", bg: "bg-amber-50 border-amber-200", description: "Some processes exist but are inconsistently applied. Audits would be challenging." },
  { level: 3, label: "Defined", range: [23, 28], color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200", description: "Core workflows are documented and followed. Evidence is available but may not be centralised." },
  { level: 4, label: "Managed", range: [29, 34], color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200", description: "Robust processes with measurement and tooling. Ready for enterprise-scale AI deployment." },
  { level: 5, label: "Optimised", range: [35, 40], color: "text-brand", bg: "bg-brand/5 border-brand/20", description: "Continuous improvement with automated enforcement, full auditability, and real-time governance metrics." },
];

function getLevel(score) {
  return LEVELS.find((l) => score >= l.range[0] && score <= l.range[1]) || LEVELS[0];
}

function getDomainScore(answers, domain) {
  const qs = QUESTIONS.filter((q) => q.domain === domain);
  const answered = qs.filter((q) => answers[q.id] !== undefined);
  if (!answered.length) return 0;
  const total = answered.reduce((s, q) => s + answers[q.id], 0);
  return Math.round((total / (answered.length * 4)) * 100);
}

function getGaps(answers) {
  return QUESTIONS.filter((q) => answers[q.id] !== undefined && answers[q.id] <= 2)
    .sort((a, b) => answers[a.id] - answers[b.id])
    .slice(0, 2);
}

export default function GovernanceMaturity() {
  useSEO({
    title: "AI Governance Maturity Assessment — Free Tool — ProofMark",
    description:
      "10-question assessment that scores your enterprise AI governance programme across classification, review process, and governance domains. Get a Level 1–5 score and gap analysis.",
    path: "/tools/ai-governance-maturity",
  });

  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const answered = Object.keys(answers).length;
  const totalScore = Object.values(answers).reduce((s, v) => s + v, 0);
  const level = getLevel(totalScore);

  const radarData = ["Classification", "Review Process", "Governance"].map((d) => ({
    domain: d,
    score: getDomainScore(answers, d),
    fullMark: 100,
  }));

  const gaps = getGaps(answers);

  return (
    <ToolLayout>
      <div className="mb-8">
        <span className="tracked-caps">Free Tool</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-3">
          AI Governance Maturity Assessment
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl">
          10 questions across three domains. Takes about 3 minutes. Get a Level 1–5 score and
          see exactly where your programme needs work.
        </p>
      </div>

      {!submitted ? (
        <div className="space-y-8">
          {QUESTIONS.map((q, idx) => (
            <div key={q.id} className="border border-border rounded-lg p-6 bg-card">
              <div className="flex items-start gap-3 mb-4">
                <span className="tracked-caps shrink-0 mt-0.5">{idx + 1}/10</span>
                <p className="font-medium leading-snug">{q.text}</p>
              </div>
              <div className="space-y-2 ml-10">
                {q.options.map((opt) => (
                  <label
                    key={opt.score}
                    className={`flex items-start gap-3 cursor-pointer rounded-md px-3 py-2.5 border transition-colors ${
                      answers[q.id] === opt.score
                        ? "border-brand bg-brand/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`q${q.id}`}
                      value={opt.score}
                      checked={answers[q.id] === opt.score}
                      onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt.score }))}
                      className="mt-0.5 accent-brand shrink-0"
                    />
                    <span className="text-sm leading-snug">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{answered}/10 questions answered</span>
            <button
              disabled={answered < 10}
              onClick={() => setSubmitted(true)}
              className="bg-brand text-brand-foreground px-6 py-2.5 rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Get my score →
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Level result */}
          <div className={`border rounded-lg p-8 ${level.bg}`}>
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <span className="tracked-caps mb-2 block">Your governance level</span>
                <div className="flex items-baseline gap-3">
                  <span className={`font-serif-display text-6xl font-light ${level.color}`}>
                    {level.level}
                  </span>
                  <span className={`text-2xl font-medium ${level.color}`}>{level.label}</span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground max-w-md">{level.description}</p>
              </div>
              <div className="text-right">
                <span className="tracked-caps mb-1 block">Score</span>
                <span className="font-mono text-3xl">{totalScore}/40</span>
              </div>
            </div>
          </div>

          {/* Radar chart */}
          <div className="border border-border rounded-lg p-6 bg-card">
            <p className="tracked-caps mb-4">Domain breakdown</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="domain" tick={{ fontSize: 12 }} />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="hsl(150 45% 26%)"
                    fill="hsl(150 45% 26%)"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gaps */}
          {gaps.length > 0 && (
            <div className="border border-border rounded-lg p-6 bg-card">
              <p className="tracked-caps mb-4">Top gaps to address</p>
              <div className="space-y-4">
                {gaps.map((q) => (
                  <div key={q.id} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-rose-400 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{q.text}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        You answered:{" "}
                        {q.options.find((o) => o.score === answers[q.id])?.label}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <PostResultCTA
            openLoop={`You scored ${totalScore}/40 — Level ${level.level}: ${level.label}. Teams at this level typically have ${level.level <= 2 ? "4–6" : level.level === 3 ? "2–4" : "1–2"} ungoverned sources feeding their AI right now. ProofMark closes the specific gaps your weakest domains exposed.`}
            source="governance-maturity"
            nextPath="/glossary/ai-knowledge-governance"
            nextLabel="Read: what AI knowledge governance actually means →"
            testimonial={null}
          />

          <button
            onClick={() => { setAnswers({}); setSubmitted(false); }}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Retake assessment
          </button>
        </div>
      )}

      {/* Educational content */}
      <div className="mt-20 space-y-10 border-t border-border/80 pt-12">
        <section>
          <h2 className="font-serif-display text-2xl mb-3">What is AI governance maturity?</h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            AI governance maturity describes how systematically an organisation controls, audits,
            and improves the knowledge and data that drives its AI systems. At Level 1, AI
            behaviour is difficult to explain or audit. At Level 5, every piece of knowledge that
            enters an AI system has a documented lineage, review trail, and revocation path.
          </p>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">The five levels of knowledge governance</h2>
          <div className="space-y-3 max-w-3xl">
            {LEVELS.map((l) => (
              <div key={l.level} className="flex gap-4">
                <span className={`font-mono font-medium shrink-0 ${l.color}`}>L{l.level}</span>
                <div>
                  <span className="font-medium">{l.label} — </span>
                  <span className="text-muted-foreground text-sm">{l.description}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            Why maturity level matters for enterprise AI compliance
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            Regulations like the EU AI Act, NIST AI RMF, and sector-specific frameworks (ISO 42001,
            SOC 2 AI criteria) all require organisations to demonstrate that their AI systems are
            built on traceable, auditable knowledge. A Level 1 programme cannot satisfy these
            requirements. Level 3 is the minimum for passing an external audit. Level 4–5
            organisations can move faster with AI because governance is a feature, not a drag.
          </p>
        </section>
      </div>
    </ToolLayout>
  );
}
